import { useEffect, useState } from 'react';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Paper, Flex, Text, Box } from '@mantine/core';
import { IconGripVertical } from '@tabler/icons-react';
import { trpc } from '../lib/trpc';

interface SortableRowProps {
  id: string;
  label: string;
}

function SortableRow({ id, label }: SortableRowProps) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });

  return (
    <Paper
      ref={setNodeRef}
      withBorder
      p="sm"
      mb="xs"
      bg={isDragging ? 'gray.1' : undefined}
      style={{
        transform: CSS.Transform.toString(transform),
        transition,
      }}
    >
      <Flex align="center" gap="sm">
        <Box {...attributes} {...listeners} style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex' }}>
          <IconGripVertical size={18} />
        </Box>
        <Text>{label}</Text>
      </Flex>
    </Paper>
  );
}

interface SortableGridItem {
  id: number;
  [key: string]: unknown;
}

interface SortableGridProps {
  entity: string;
  items: SortableGridItem[];
  labelField: string;
  onReorderComplete: () => void;
}

export default function SortableGrid({ entity, items: initialItems, labelField, onReorderComplete }: SortableGridProps) {
  const [items, setItems] = useState(initialItems);

  useEffect(() => {
    setItems(initialItems);
  }, [initialItems]);

  const reorderMutation = trpc.admin.crud.reorder.useMutation({ onSuccess: onReorderComplete });

  const sensors = useSensors(useSensor(PointerSensor));

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = items.findIndex((item) => String(item.id) === String(active.id));
    const newIndex = items.findIndex((item) => String(item.id) === String(over.id));
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(items, oldIndex, newIndex);
    setItems(reordered);

    const reorderPayload = reordered.map((item, index) => ({ id: item.id, sort: index + 1 }));
    reorderMutation.mutate({ entity, items: reorderPayload });
  }

  return (
    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
      <SortableContext items={items.map((i) => String(i.id))} strategy={verticalListSortingStrategy}>
        {items.map((item) => (
          <SortableRow
            key={item.id}
            id={String(item.id)}
            label={String(item[labelField] ?? item.id)}
          />
        ))}
      </SortableContext>
    </DndContext>
  );
}
